import React, {createContext, useContext, useMemo} from 'react';

import {type SettingType} from '../../../services/analytics/workspaceSettingsEvents';
import {type DispatchableAction} from '../../../types/redux';
import {type ColorPaletteOption} from '../../../util/colors';
import {useDetectChanges} from '../../../util/react-help/detectReactPropChanges';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {
  useColorPaletteOption,
  useLinePlotSettings,
  useSemanticLegendPalette,
  useSemanticLegendRunColorOptions,
  useTrackWorkspaceSettingChange,
  useUpdateAllLinePlotSectionSettings,
  useUpdateColorPaletteOption,
  useUpdateLayoutSettingAndOrganizePrefix,
  useUpdateLayoutSettingAndSortPanels,
  useUpdateLinePlotWorkspaceSettings,
  useUpdateSemanticLegendPalette,
  useUpdateShouldAutoGeneratePanels,
  useUpdateWorkspaceLayoutSettings,
  useWorkspaceLayoutSettings,
} from './hooks';
import {
  SEMANTIC_LEGEND_PALETTE_DEFAULT,
  type SemanticLegendPalette,
} from './semanticLegend/utils/colorPalettes';
import {
  type Complete,
  type LinePlotSettings,
  type OrganizationPrefix,
  type RunColorOptions,
  type WorkspaceLayoutSettings,
} from './types';

export type WorkspaceSettingsContextType = {
  // settings
  workspaceLayout: Complete<WorkspaceLayoutSettings>;
  linePlot: Complete<LinePlotSettings>;
  colorPaletteOption: ColorPaletteOption | undefined;
  semanticLegendRunColorOptions: RunColorOptions | undefined;
  semanticLegendPalette: SemanticLegendPalette | undefined;
  // updaters
  updateSemanticLegendPalette: (
    palette: SemanticLegendPalette
  ) => DispatchableAction;
  updateWorkspaceLayoutSettings: (
    settings: Partial<WorkspaceLayoutSettings>
  ) => DispatchableAction;
  updateLinePlotWorkspaceSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  updateAllLinePlotSectionSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  updateShouldAutoGeneratePanels: (shouldEnable: boolean) => void;
  updateLayoutSettingAndSortPanels: (sortAlphabetically: boolean) => void;
  updateLayoutSettingAndOrganizePrefix: (
    autoOrganizePrefix: OrganizationPrefix
  ) => void;
  updateColorPaletteOption: (
    colorPaletteOption: ColorPaletteOption | undefined
  ) => void;

  // analytics
  trackWorkspaceSettingChange: (
    setting: SettingType,
    value: string | undefined
  ) => void;
};

export const WorkspaceSettingsContext = createContext<
  WorkspaceSettingsContextType | undefined
>(undefined);

type WorkspaceSettingsProps = {
  children: React.ReactNode;
};

/**
 * This context provider is used in conjuction with SectionSettingsContextProvider.
 * We use these two context providers to handle cascading settings logic.
 */
export const WorkspaceSettingsContextProvider = ({
  children,
}: WorkspaceSettingsProps) => {
  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  const linePlotSettings = useLinePlotSettings();
  const semanticLegendRunColorOptions = useSemanticLegendRunColorOptions();
  const workspaceLayout = useWorkspaceLayoutSettings();
  const colorPaletteOption = useColorPaletteOption();
  const semanticLegendPalette = useSemanticLegendPalette();

  // line plot related settings
  const updateLinePlotWorkspaceSettings = useUpdateLinePlotWorkspaceSettings();
  const updateAllLinePlotSectionSettings =
    useUpdateAllLinePlotSectionSettings();
  const updateColorPaletteOption = useUpdateColorPaletteOption();
  const updateSemanticLegendPalette = useUpdateSemanticLegendPalette();

  const updateWorkspaceLayoutSettings = useUpdateWorkspaceLayoutSettings();
  const updateShouldAutoGeneratePanels = useUpdateShouldAutoGeneratePanels();
  const updateLayoutSettingAndSortPanels =
    useUpdateLayoutSettingAndSortPanels();
  const updateLayoutSettingAndOrganizePrefix =
    useUpdateLayoutSettingAndOrganizePrefix();
  const trackWorkspaceSettingChange = useTrackWorkspaceSettingChange();

  const value = useMemo(
    () => ({
      // settings
      workspaceLayout,
      linePlot: linePlotSettings,
      colorPaletteOption,
      semanticLegendRunColorOptions,
      semanticLegendPalette,

      // updaters
      updateWorkspaceLayoutSettings,
      updateLinePlotWorkspaceSettings,
      updateAllLinePlotSectionSettings,
      updateLayoutSettingAndSortPanels,
      updateShouldAutoGeneratePanels,
      updateLayoutSettingAndOrganizePrefix,
      updateColorPaletteOption,
      updateSemanticLegendPalette,
      // analytics
      trackWorkspaceSettingChange,
    }),
    [
      workspaceLayout,
      linePlotSettings,
      colorPaletteOption,
      semanticLegendPalette,
      semanticLegendRunColorOptions,
      updateWorkspaceLayoutSettings,
      updateLinePlotWorkspaceSettings,
      updateAllLinePlotSectionSettings,
      updateLayoutSettingAndSortPanels,
      updateShouldAutoGeneratePanels,
      updateLayoutSettingAndOrganizePrefix,
      updateColorPaletteOption,
      updateSemanticLegendPalette,
      trackWorkspaceSettingChange,
    ]
  );

  useDetectChanges('WorkspaceSettingsContextProvider', {
    linePlotSettings,
    panelBankConfigRef,
    shouldAutoGeneratePanels: workspaceLayout.shouldAutoGeneratePanels,
    updateAllLinePlotSectionSettings,
    updateLinePlotWorkspaceSettings,
    updateShouldAutoGeneratePanels,
    workspaceSettingsRef,
  });

  return (
    <WorkspaceSettingsContext.Provider value={value}>
      {children}
    </WorkspaceSettingsContext.Provider>
  );
};

export const useWorkspaceSettingsContext = (): WorkspaceSettingsContextType => {
  const value = useContext(WorkspaceSettingsContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceSettingsContext must be used within a WorkspaceSettingsContextProvider'
    );
  }

  return value;
};
